import { useEffect, useRef, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin-only "push" notifier using the browser Notification API (no VAPID / service worker
 * required — fires while the tab is open or backgrounded).
 *
 * Polls /api/admin/moderation-counts every POLL_MS. When the pending photo queue OR pending
 * reports increase past the last seen value, a native browser notification is triggered.
 * Shows a small bell toggle in the top-right corner of the admin panel so the admin can
 * grant / revoke permission.
 */

const POLL_MS = 30_000; // 30s
const LS_KEY = "pk2_admin_push_v1";

function loadSettings() {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return {
      enabled: s.enabled !== false, // default on
      lastPhotos: typeof s.lastPhotos === "number" ? s.lastPhotos : 0,
      lastReports: typeof s.lastReports === "number" ? s.lastReports : 0,
    };
  } catch {
    return { enabled: true, lastPhotos: 0, lastReports: 0 };
  }
}

function saveSettings(patch) {
  try {
    const cur = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch { /* ignore */ }
}

export default function AdminPushNotifier() {
  const initial = useRef(loadSettings()).current;
  const [enabled, setEnabled] = useState(initial.enabled);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const lastRef = useRef({ photos: initial.lastPhotos, reports: initial.lastReports });

  useEffect(() => { saveSettings({ enabled }); }, [enabled]);

  // Ask for permission on mount if enabled and not yet decided
  useEffect(() => {
    if (!enabled) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().then((p) => setPermission(p)).catch(() => {});
    }
  }, [enabled]);

  // Poll moderation counts
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/moderation-counts");
        if (cancelled) return;
        const prev = lastRef.current;
        const photos = data.photos_pending || 0;
        const reports = data.reports_pending || 0;
        const hidden = data.auto_hidden_profiles || 0;

        const canNotify =
          typeof Notification !== "undefined" &&
          Notification.permission === "granted" &&
          enabled;

        // Only notify when count GREW since last tick (new items arrived).
        const newPhotos = photos > prev.photos;
        const newReports = reports > prev.reports;
        if (canNotify && (newPhotos || newReports)) {
          const parts = [];
          if (newPhotos) parts.push(`${photos} fotos pendientes`);
          if (newReports) parts.push(`${reports} reportes pendientes`);
          if (hidden > 0) parts.push(`${hidden} auto-ocultos`);
          try {
            const n = new Notification("PK2 Scarlett · Moderación", {
              body: parts.join(" · "),
              tag: "pk2-mod-queue",
              icon: "/icon-192.png",
              badge: "/icon-192.png",
              requireInteraction: reports > 0, // reports are more urgent
            });
            n.onclick = () => {
              window.focus();
              // Route to the relevant tab
              const target = newReports ? "reports" : "photos";
              window.location.assign(`/admin?tab=${target}`);
              n.close();
            };
          } catch { /* browser blocked */ }
        }

        lastRef.current = { photos, reports };
        saveSettings({ lastPhotos: photos, lastReports: reports });
      } catch { /* ignore transient errors */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  const toggle = async () => {
    if (!enabled) {
      // turning ON → request permission right away
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        try {
          const p = await Notification.requestPermission();
          setPermission(p);
        } catch { /* ignore */ }
      }
      setEnabled(true);
    } else {
      setEnabled(false);
    }
  };

  if (typeof Notification === "undefined") return null;

  const label = enabled
    ? permission === "granted" ? "Push ON"
      : permission === "denied" ? "Permiso bloqueado"
      : "Activando…"
    : "Push OFF";

  return (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex items-center gap-2 text-xs px-3 py-2 uppercase tracking-[0.15em]"
      style={{
        border: `1px solid ${enabled && permission === "granted" ? "var(--brand-primary)" : "var(--border-subtle)"}`,
        background: enabled && permission === "granted" ? "rgba(255,0,51,0.08)" : "var(--bg-elevated)",
        color: enabled && permission === "granted" ? "var(--brand-primary)" : "var(--text-secondary)",
      }}
      data-testid="admin-push-toggle"
      title={enabled ? "Desactivar notificaciones push" : "Activar notificaciones push"}
    >
      {enabled ? <Bell size={14} /> : <BellOff size={14} />} {label}
    </button>
  );
}
